import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button, confirm, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, CheckIcon, EditIcon, TrashIcon } from "lucide-react";
import { useState } from "react";
import { useCancelRoomBooking, useRoomBookingQuery } from "../api/queries";
import { CompleteRoomBookingDialog } from "../ui/CompleteRoomBookingDialog";
import { RoomBookingAssistInstructorsSection } from "../ui/RoomBookingAssistInstructorsSection";

export function RoomBookingDetailPage() {
  const { id } = useParams({ from: "/app/room-bookings/$id" });
  const nav = useNavigate({ from: "/app/room-bookings/$id" });
  const { data: booking, isLoading } = useRoomBookingQuery(id ?? "");
  const canEdit = useActionPermission(["room-bookings:update"]);
  const canCancel = useActionPermission(["room-bookings:cancel"]);
  const cancelBooking = useCancelRoomBooking();
  const [completeOpen, setCompleteOpen] = useState(false);

  const roomBookingDetailBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ການຈອງຫ້ອງ", to: "/app/room-bookings" },
        { label: "ລາຍລະອຽດ" },
      ]}
    />
  );

  if (isLoading || !booking) {
    return (
      <>
        <Header>{roomBookingDetailBreadcrumb}</Header>
        <Main>
          <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
        </Main>
      </>
    );
  }

  const statusLabel =
    booking.status === "scheduled"
      ? "ຈອງແລ້ວ"
      : booking.status === "completed"
        ? "ສຳເລັດ"
        : "ຍົກເລີກ";

  return (
    <>
      <Header>{roomBookingDetailBreadcrumb}</Header>

      <Main>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ລາຍລະອຽດການຈອງຫ້ອງ
            </h2>
            <p className="text-muted-foreground">
              {booking.roomName} · {formatDate(booking.date)} ·{" "}
              {formatTime(booking.startTime)}–{formatTime(booking.endTime)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/room-bookings" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {canEdit && booking.status === "scheduled" && (
              <>
                <Button variant="outline" onClick={() => setCompleteOpen(true)}>
                  <CheckIcon className="size-4" />
                  ສຳເລັດ
                </Button>
                <Button
                  onClick={() =>
                    nav({
                      to: "/app/room-bookings/$id/edit",
                      params: { id: id ?? "" },
                    })
                  }
                >
                  <EditIcon className="size-4" />
                  ແກ້ໄຂ
                </Button>
              </>
            )}
            {canCancel &&
              (booking.status === "scheduled" ||
                booking.status === "completed") && (
                <Button
                  variant="destructive"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "ຍົກເລີກການຈອງຫ້ອງ",
                      description:
                        booking.status === "completed"
                          ? "ການຈອງນີ້ສຳເລັດແລ້ວ — ການຍົກເລີກຈະບໍ່ນັບຄ່າຈ້າງຄູຊ່ວຍສອນ (ຖ້າມີ). ທ່ານແນ່ໃຈບໍ?"
                          : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການຈອງນີ້?",
                      actionText: "ຍົກເລີກ",
                      ActionProps: { variant: "destructive" },
                    });
                    if (!ok) return;
                    toast.promise(cancelBooking.mutateAsync(booking.id), {
                      loading: "ກຳລັງຍົກເລີກ...",
                      success: "ຍົກເລີກການຈອງສຳເລັດ",
                      error: (e) =>
                        e instanceof Error
                          ? e.message
                          : "ຍົກເລີກການຈອງລົ້ມເຫຼວ",
                    });
                  }}
                >
                  <TrashIcon className="size-4" />
                  ຍົກເລີກ
                </Button>
              )}
          </div>
        </div>

        <div className="rounded-xl border bg-card p-6">
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground text-sm">ຫ້ອງ</dt>
              <dd className="font-medium">{booking.roomName}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">ວັນທີ</dt>
              <dd className="font-medium">{formatDate(booking.date)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">ເວລາ</dt>
              <dd className="font-medium">
                {formatTime(booking.startTime)} – {formatTime(booking.endTime)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-sm">ສະຖານະ</dt>
              <dd className="font-medium">{statusLabel}</dd>
            </div>
            {booking.title && (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground text-sm">ຊື່ / ວັດຖຸປະສົງ</dt>
                <dd className="font-medium">{booking.title}</dd>
              </div>
            )}
            {booking.contactName && (
              <div>
                <dt className="text-muted-foreground text-sm">ຜູ້ຈອງ</dt>
                <dd className="font-medium">
                  {booking.contactName}
                  {booking.contactPhone && ` · ${booking.contactPhone}`}
                </dd>
              </div>
            )}
            {booking.amount != null && (
              <div>
                <dt className="text-muted-foreground text-sm">ຄ່າເຊົ່າ</dt>
                <dd className="font-medium">
                  {booking.amount.toLocaleString()} ກີບ
                </dd>
              </div>
            )}
            {booking.notes && (
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground text-sm">ໝາຍເຫດ</dt>
                <dd className="font-medium">{booking.notes}</dd>
              </div>
            )}
          </dl>
        </div>

        <RoomBookingAssistInstructorsSection
          bookingId={booking.id}
          bookingDate={booking.date}
          bookingStatus={booking.status}
          canEdit={!!canEdit}
        />
      </Main>

      <CompleteRoomBookingDialog
        bookingId={booking.id}
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        onSuccess={() => {
          toast.success("ສຳເລັດການຈອງແລ້ວ");
        }}
      />
    </>
  );
}
